import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";

let ytApi = null;
function loadYT() {
  if (!ytApi) {
    ytApi = new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve(window.YT);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT); };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    });
  }
  return ytApi;
}

const fmt = (t) => { t = Math.max(0, Math.floor(t || 0)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`; };

export function PressVideo({ id, medio, title, videoTitle }) {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const [muted, setMuted] = useState(false);
  const [prog, setProg] = useState({ cur: 0, dur: 0 });
  const [topCover, setTopCover] = useState(false);
  const wrapRef = useRef(null);
  const holderRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const coverT = useRef(null);

  const hideTitleSoon = () => {
    setTopCover(true);
    clearTimeout(coverT.current);
    coverT.current = setTimeout(() => setTopCover(false), 4500);
  };

  const reset = () => {
    clearInterval(pollRef.current);
    try { if (playerRef.current) playerRef.current.destroy(); } catch (e) {}
    playerRef.current = null;
    setProg({ cur: 0, dur: 0 });
    setState("idle");
  };

  const start = async () => {
    if (state !== "idle") return;
    setState("loading");
    const YT = await loadYT();
    playerRef.current = new YT.Player(holderRef.current, {
      videoId: id,
      playerVars: { autoplay: 1, controls: 0, rel: 0, playsinline: 1, fs: 0, disablekb: 1, iv_load_policy: 3, origin: window.location.origin },
      events: {
        onReady: (e) => {
          try {
            e.target.unloadModule("captions");
            e.target.unloadModule("cc");
            e.target.playVideo();
          } catch (err) {}
        },
        onStateChange: (e) => {
          if (e.data === window.YT.PlayerState.PLAYING) {
            try { e.target.unloadModule("captions"); e.target.unloadModule("cc"); } catch (err) {}
            setState("playing"); hideTitleSoon();
          }
          else if (e.data === window.YT.PlayerState.PAUSED) setState("paused");
          else if (e.data === window.YT.PlayerState.ENDED) reset();
        },
      },
    });
    // Si el autoplay fue bloqueado, mostrar controles propios igualmente
    setTimeout(() => setState((s) => (s === "loading" ? "paused" : s)), 3500);
    pollRef.current = setInterval(() => {
      const p = playerRef.current;
      if (p && typeof p.getCurrentTime === "function") {
        setProg({ cur: p.getCurrentTime() || 0, dur: p.getDuration() || 0 });
      }
    }, 500);
  };

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearTimeout(coverT.current);
    try { if (playerRef.current) playerRef.current.destroy(); } catch (e) {}
  }, []);

  const toggle = () => {
    const p = playerRef.current;
    if (!p) return;
    if (state === "playing") p.pauseVideo(); else p.playVideo();
  };
  const seek = (e) => {
    e.stopPropagation();
    const p = playerRef.current;
    if (!p || !prog.dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    p.seekTo(((e.clientX - r.left) / r.width) * prog.dur, true);
    hideTitleSoon();
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    const p = playerRef.current;
    if (!p) return;
    if (muted) { p.unMute(); setMuted(false); } else { p.mute(); setMuted(true); }
  };
  const fullscreen = (e) => {
    e.stopPropagation();
    if (document.fullscreenElement) document.exitFullscreen();
    else if (wrapRef.current && wrapRef.current.requestFullscreen) wrapRef.current.requestFullscreen();
  };

  const active = state === "playing" || state === "paused";

  return (
    <article className="bg-white rounded-2xl overflow-hidden border transition-shadow hover:shadow-lg" style={{ borderColor: "var(--border-medium)" }} data-testid={`press-video-${id}`}>
      <div ref={wrapRef} className="relative" style={{ aspectRatio: "16/9", background: "#0A1F36" }}>
        <div className="absolute inset-0"><div ref={holderRef} className="w-full h-full" /></div>

        {active && (
          <div className="absolute inset-0 cursor-pointer select-none" onClick={toggle} data-testid={`press-overlay-${id}`}>
            {/* Tapa superior: oculta foto/nombre del canal y muestra el título del video */}
            <div
              className="absolute top-0 left-0 right-0 transition-opacity duration-500 pointer-events-none flex items-start px-4 pt-3"
              style={{ height: 84, background: "linear-gradient(180deg, #0A1F36 0%, #0A1F36 62%, rgba(10,31,54,0) 100%)", opacity: state === "paused" || topCover ? 1 : 0 }}
            >
              <span className="text-white text-[13px] md:text-[14px] font-bold leading-snug line-clamp-2" style={{ fontFamily: "Manrope", textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>
                {videoTitle || title}
              </span>
            </div>
            {state === "paused" && (
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--brand-accent)", boxShadow: "0 8px 30px rgba(238,81,71,0.45)" }}>
                  <Play size={24} color="white" fill="white" style={{ marginLeft: 3 }} />
                </span>
              </span>
            )}
            {/* Controles propios */}
            <div className="absolute bottom-0 left-0 right-0 px-3 pb-2 pt-7 flex items-center gap-2.5" style={{ background: "linear-gradient(0deg, rgba(10,31,54,0.95) 0%, rgba(10,31,54,0.6) 55%, rgba(10,31,54,0) 100%)" }}>
              <button onClick={(e) => { e.stopPropagation(); toggle(); }} className="text-white hover:opacity-80" aria-label="Reproducir o pausar" data-testid={`press-toggle-${id}`}>
                {state === "playing" ? <Pause size={16} fill="white" /> : <Play size={16} fill="white" />}
              </button>
              <span className="text-white text-[10.5px] font-semibold whitespace-nowrap" style={{ fontVariantNumeric: "tabular-nums" }}>
                {fmt(prog.cur)} / {fmt(prog.dur)}
              </span>
              <div className="flex-1 h-5 flex items-center cursor-pointer" onClick={seek} data-testid={`press-seek-${id}`}>
                <div className="w-full h-[4px] rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.25)" }}>
                  <div className="h-full rounded-full" style={{ width: `${prog.dur ? (prog.cur / prog.dur) * 100 : 0}%`, background: "var(--brand-accent)" }} />
                </div>
              </div>
              <button onClick={toggleMute} className="text-white hover:opacity-80" aria-label="Silenciar">
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <button onClick={fullscreen} className="text-white hover:opacity-80" aria-label="Pantalla completa">
                <Maximize2 size={15} />
              </button>
            </div>
          </div>
        )}

        {!active && (
          <button onClick={start} className="group absolute inset-0 w-full h-full" aria-label={`Reproducir: ${title}`} data-testid={`press-play-${id}`}>
            <img
              src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`}
              onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`; }}
              alt={title}
              className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
              draggable={false}
            />
            <span className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(10,31,54,0.55) 100%)" }} />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "var(--brand-accent)", boxShadow: "0 8px 30px rgba(238,81,71,0.45)", opacity: state === "loading" ? 0.6 : 1 }}>
                <Play size={24} color="white" fill="white" style={{ marginLeft: 3 }} />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="px-5 py-4 flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: "rgba(238,81,71,0.08)", border: "1px solid rgba(238,81,71,0.25)", color: "var(--brand-accent)" }}>
          {medio}
        </span>
        <span className="text-[13.5px] font-bold" style={{ fontFamily: "Manrope" }}>{title}</span>
      </div>
    </article>
  );
}

export default PressVideo;
